import React, { useEffect, useState } from "react";
import Layouts from "../Layouts";
import { Spin, Empty } from "antd";
import { GoDotFill } from "react-icons/go";
import {
    MdReceipt,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
} from "react-icons/md";
import { getCurrentUser } from "../../utils/auth";
import CartPlaceService from "../../Redux/Services/CartPlaceService";

function MyOrder() {
    const [orders, setOrders] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);

    // Which order is currently expanded
    const [expandedOrder, setExpandedOrder] = useState(null);

    useEffect(() => {
        const fetchOrders = async () => {
            const user = getCurrentUser();

            if (!user) return;

            const userId =
                user.userId ||
                user.userCode ||
                localStorage.getItem("userCode");

            if (!userId) return;

            setLoading(true);

            try {
                const response =
                    await CartPlaceService.getMyOrderList(userId);

                if (response?.data) {
                    setOrders(response.data);
                }
            } catch (err) {
                console.error("Failed to load orders:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, []);

    const filteredOrders = orders.filter((order) => {
        const query = searchQuery.toLowerCase();

        return (
            (order.productName || "").toLowerCase().includes(query) ||
            (order.productCode || "").toLowerCase().includes(query) ||
            (order.txnId || "").toLowerCase().includes(query) ||
            (order.productCategoryCode || "")
                .toLowerCase()
                .includes(query)
        );
    });

    const getImgUrl = (imgName) => {
        if (!imgName) return "";

        if (
            imgName.startsWith("http") ||
            imgName.startsWith("data:")
        ) {
            return imgName;
        }

        return `${process.env.REACT_APP_IMAGE_URL || ""}${imgName}`;
    };

    // Toggle accordion
    const toggleOrder = (orderId) => {
        setExpandedOrder((prev) =>
            prev === orderId ? null : orderId
        );
    };

    return (
        <Layouts>
            <main className="min-h-screen bg-[#FAFAF8]">

                {/* =====================================================
                    PAGE CONTAINER
                ====================================================== */}
                <div className=" w-full max-w-7xl mx-auto px-3 xs:px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 py-6 sm:py-10 md:py-14 ">

                    {/* =================================================
                        PAGE HEADER
                    ================================================== */}
                    <header className=" text-center mb-7 sm:mb-10 md:mb-12
                    ">

                        <span className=" inline-block text-[9px] sm:text-xs md:text-sm uppercase tracking-[0.22em] sm:tracking-[0.35em] font-bold text-[#A17A17]">
                            Purchase History
                        </span>

                        <h1 className=" mt-2 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-[#3F342A] leading-tight ">
                            My Orders
                        </h1>

                        <div className=" flex justify-center items-center gap-2 mt-3 ">
                            <div className=" w-7 sm:w-12 md:w-16 h-[2px] bg-[#A17A17] " />

                            <div className=" w-2 h-2 rounded-full bg-[#A17A17] " />

                            <div className=" w-7 sm:w-12 md:w-16 h-[2px] bg-[#A17A17]" />
                        </div>

                        <p className=" mt-3 text-xs sm:text-sm md:text-base text-gray-500 max-w-xl mx-auto leading-6 px-3 ">
                            View and track your Ayurvedic product purchases.
                        </p>

                    </header>


                    {/* =================================================
                        LOADING
                    ================================================== */}
                    {loading ? (

                        <div className=" min-h-[280px] sm:min-h-[350px] flex flex-col justify-center items-center bg-white rounded-2xl sm:rounded-3xl border border-[#EEE7D8] shadow-sm px-4 ">

                            <Spin size="large" />

                            <p className=" text-gray-500 mt-4 font-medium text-xs sm:text-sm text-center ">
                                Loading your orders...
                            </p>

                        </div>

                    ) : filteredOrders.length === 0 ? (

                        /* =================================================
                            EMPTY STATE
                        ================================================== */
                        <div className=" min-h-[280px] sm:min-h-[350px] bg-white rounded-2xl sm:rounded-3xl border border-[#EEE7D8] px-5 sm:px-10 py-12 sm:py-16 flex items-center justify-center shadow-sm ">

                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <div className=" mt-3 sm:mt-4 text-center ">

                                        <h3 className=" text-base sm:text-lg md:text-xl font-bold text-gray-800 ">
                                            No orders found
                                        </h3>

                                        <p className=" text-xs sm:text-sm text-gray-500 mt-2 max-w-sm mx-auto leading-6 ">
                                            {searchQuery
                                                ? "We couldn't find any orders matching your query."
                                                : "You haven't placed any orders yet."
                                            }
                                        </p>

                                    </div>
                                }
                            />

                        </div>

                    ) : (

                        /* =================================================
                            ORDERS
                        ================================================== */
                        <div className="
                            space-y-4
                            sm:space-y-5
                            md:space-y-6
                        ">

                            {filteredOrders.map((order, index) => {

                                const orderId = order.id || index;

                                const formattedName =
                                    (
                                        order.productName ||
                                        order.productCode ||
                                        "PRODUCT"
                                    ).replace(/_/g, " ");

                                const imageSrc =
                                    getImgUrl(order.productImage);

                                const isDelivered =
                                    order.status === 1;

                                const isExpanded =
                                    expandedOrder === orderId;

                                return (

                                    <article
                                        key={orderId}
                                        className={` bg-white border border-[#EEE9DF] rounded-2xl sm:rounded-3xl shadow-[0_6px_25px_rgba(0,0,0,0.035)] overflow-hidden transition-all duration-300
                                            ${isExpanded
                                                ? "shadow-[0_12px_35px_rgba(161,122,23,0.09)]"
                                                : "hover:shadow-[0_10px_30px_rgba(161,122,23,0.06)]"
                                            }
                                        `}
                                    >

                                        {/* =================================================
                                            MAIN COMPACT ORDER ROW
                                        ================================================== */}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                toggleOrder(orderId)
                                            }
                                            className=" w-full text-left p-3 sm:p-5 md:p-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A17A17] focus-visible:ring-inset ">

                                            <div className=" flex items-center gap-3 sm:gap-5 ">

                                                {/* PRODUCT IMAGE */}
                                                <div className=" w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 flex-shrink-0 bg-[#FAF9F6] rounded-xl sm:rounded-2xl border border-[#EEE8DC] flex items-center justify-center overflow-hidden ">

                                                    {imageSrc ? (

                                                        <img
                                                            src={imageSrc}
                                                            alt={formattedName}
                                                            className=" w-full h-full object-contain p-1.5 sm:p-2 " />
                                                    ) : (

                                                        <span className=" text-[8px] sm:text-[10px] text-gray-400 text-center font-bold px-1 ">
                                                            {order.productCategoryCode ||
                                                                "PRODUCT"}
                                                        </span>

                                                    )}

                                                </div>


                                                {/* =================================================
                                                    IMPORTANT PRODUCT INFORMATION
                                                ================================================== */}
                                                <div className="
                                                    flex-1
                                                    min-w-0
                                                ">

                                                    <span className="
                                                        hidden
                                                        sm:block
                                                        text-[9px]
                                                        uppercase
                                                        tracking-[0.18em]
                                                        font-bold
                                                        text-[#A17A17]
                                                        mb-1
                                                    ">
                                                        Product
                                                    </span>

                                                    <h2 className="
                                                        text-sm
                                                        sm:text-base
                                                        md:text-lg
                                                        font-bold
                                                        text-[#332B25]
                                                        leading-snug
                                                        break-words
                                                        line-clamp-2
                                                    ">
                                                        {formattedName}
                                                    </h2>


                                                    {/* Category */}
                                                    <div className="
                                                        flex
                                                        flex-wrap
                                                        items-center
                                                        gap-2
                                                        mt-1.5
                                                        sm:mt-2
                                                    ">

                                                        {order.productCategoryCode && (

                                                            <span className="
                                                                text-[8px]
                                                                sm:text-[9px]
                                                                uppercase
                                                                font-semibold
                                                                tracking-wider
                                                                text-gray-400
                                                            ">
                                                                {order.productCategoryCode}
                                                            </span>

                                                        )}

                                                        {order.packageSize && (
                                                            <span className=" inline-flex items-center max-w-[100px] sm:max-w-none px-2 py-1 rounded-md bg-[#FFF5D6] text-[#A17A17] text-[9px] sm:text-[10px] font-bold whitespace-nowrap overflow-hidden text-ellipsis ">
                                                                {order.packageSize}
                                                            </span>
                                                        )}

                                                    </div>


                                                    {/* =================================================
                                                        MOBILE IMPORTANT INFO
                                                    ================================================== */}
                                                    <div className="
                                                        flex
                                                        flex-wrap
                                                        gap-x-3
                                                        gap-y-1
                                                        mt-2
                                                    ">

                                                        <span className="
                                                            text-[10px]
                                                            sm:text-xs
                                                            text-gray-500
                                                        ">
                                                            Qty:
                                                            <strong className="
                                                                ml-1
                                                                text-gray-800
                                                            ">
                                                                {order.qty || 1}
                                                            </strong>
                                                        </span>

                                                        <span className="
                                                            text-[10px]
                                                            sm:text-xs
                                                            text-gray-500
                                                        ">
                                                            Paid:
                                                            <strong className="
                                                                ml-1
                                                                text-[#A17A17]
                                                                font-bold
                                                            ">
                                                                INR{" "}
                                                                {order.paidAmount ||
                                                                    order.amount}
                                                            </strong>
                                                        </span>

                                                    </div>

                                                </div>


                                                {/* =================================================
                                                    STATUS + ARROW
                                                ================================================== */}
                                                <div className="
                                                    flex
                                                    flex-col
                                                    items-center
                                                    justify-center
                                                    gap-1
                                                    flex-shrink-0
                                                ">

                                                    <div className="
                                                        hidden
                                                        sm:flex
                                                        items-center
                                                        gap-1
                                                    ">

                                                        <GoDotFill
                                                            className={`
                                                                text-xs
                                                                ${isDelivered
                                                                    ? "text-green-500"
                                                                    : "text-amber-500"
                                                                }
                                                            `}
                                                        />

                                                        <span className="
                                                            hidden
                                                            md:block
                                                            text-[10px]
                                                            font-semibold
                                                            text-gray-500
                                                        ">
                                                            {isDelivered
                                                                ? "Confirmed"
                                                                : "Pending"
                                                            }
                                                        </span>

                                                    </div>


                                                    {isExpanded ? (

                                                        <MdKeyboardArrowUp
                                                            className="
                                                                text-xl
                                                                sm:text-2xl
                                                                text-[#A17A17]
                                                            "
                                                        />

                                                    ) : (

                                                        <MdKeyboardArrowDown
                                                            className="
                                                                text-xl
                                                                sm:text-2xl
                                                                text-gray-400
                                                                group-hover:text-[#A17A17]
                                                            "
                                                        />

                                                    )}

                                                </div>

                                            </div>

                                        </button>


                                        {/* =================================================
                                            EXPANDED INFORMATION
                                        ================================================== */}
                                        <div
                                            className={`
                                                grid
                                                transition-all
                                                duration-300
                                                ease-in-out
                                                ${isExpanded
                                                    ? "grid-rows-[1fr] opacity-100"
                                                    : "grid-rows-[0fr] opacity-0"
                                                }
                                            `}
                                        >

                                            <div className="overflow-hidden">

                                                <div className="
                                                    border-t
                                                    border-[#F0ECE4]
                                                    bg-[#FCFBF7]
                                                    p-4
                                                    sm:p-5
                                                    md:p-6
                                                ">

                                                    <div className="
                                                        grid
                                                        grid-cols-1
                                                        sm:grid-cols-2
                                                        lg:grid-cols-3
                                                        gap-4
                                                        sm:gap-5
                                                    ">

                                                        {/* TRANSACTION ID */}
                                                        <div className="
                                                            bg-white
                                                            rounded-xl
                                                            border
                                                            border-[#EEE8DC]
                                                            p-3
                                                            sm:p-4
                                                        ">

                                                            <div className="
                                                                flex
                                                                items-center
                                                                gap-2
                                                                text-[#A17A17]
                                                            ">

                                                                <MdReceipt className="
                                                                    text-lg
                                                                " />

                                                                <span className="
                                                                    text-[9px]
                                                                    sm:text-[10px]
                                                                    uppercase
                                                                    tracking-wider
                                                                    font-bold
                                                                ">
                                                                    Transaction ID
                                                                </span>

                                                            </div>

                                                            <p className="
                                                                mt-2
                                                                text-xs
                                                                sm:text-sm
                                                                font-semibold
                                                                text-gray-700
                                                                break-all
                                                            ">
                                                                {order.txnId || "N/A"}
                                                            </p>

                                                        </div>


                                                        {/* ORDER DATE */}
                                                        <div className="
                                                            bg-white
                                                            rounded-xl
                                                            border
                                                            border-[#EEE8DC]
                                                            p-3
                                                            sm:p-4
                                                        ">

                                                            <span className="
                                                                text-[9px]
                                                                sm:text-[10px]
                                                                uppercase
                                                                tracking-wider
                                                                font-bold
                                                                text-[#A17A17]
                                                            ">
                                                                Ordered On
                                                            </span>

                                                            <p className="
                                                                mt-2
                                                                text-xs
                                                                sm:text-sm
                                                                font-semibold
                                                                text-gray-700
                                                            ">
                                                                {order.createdDate?.slice(
                                                                    0,
                                                                    10
                                                                ) || "N/A"}
                                                            </p>

                                                        </div>


                                                        {/* CATEGORY */}
                                                        <div className="
                                                            bg-white
                                                            rounded-xl
                                                            border
                                                            border-[#EEE8DC]
                                                            p-3
                                                            sm:p-4
                                                        ">

                                                            <span className="
                                                                text-[9px]
                                                                sm:text-[10px]
                                                                uppercase
                                                                tracking-wider
                                                                font-bold
                                                                text-[#A17A17]
                                                            ">
                                                                Category
                                                            </span>

                                                            <p className="
                                                                mt-2
                                                                text-xs
                                                                sm:text-sm
                                                                font-semibold
                                                                text-gray-700
                                                                break-words
                                                            ">
                                                                {order.productCategoryCode ||
                                                                    "N/A"}
                                                            </p>

                                                        </div>


                                                        {/* PACKAGE */}
                                                        <div className="
                                                            bg-white
                                                            rounded-xl
                                                            border
                                                            border-[#EEE8DC]
                                                            p-3
                                                            sm:p-4
                                                        ">

                                                            <span className="
                                                                text-[9px]
                                                                sm:text-[10px]
                                                                uppercase
                                                                tracking-wider
                                                                font-bold
                                                                text-[#A17A17]
                                                            ">
                                                                Package Size
                                                            </span>

                                                            <p className="
                                                                mt-2
                                                                text-xs
                                                                sm:text-sm
                                                                font-semibold
                                                                text-gray-700
                                                            ">
                                                                {order.packageSize ||
                                                                    "N/A"}
                                                            </p>

                                                        </div>


                                                        {/* QUANTITY */}
                                                        <div className="
                                                            bg-white
                                                            rounded-xl
                                                            border
                                                            border-[#EEE8DC]
                                                            p-3
                                                            sm:p-4
                                                        ">

                                                            <span className="
                                                                text-[9px]
                                                                sm:text-[10px]
                                                                uppercase
                                                                tracking-wider
                                                                font-bold
                                                                text-[#A17A17]
                                                            ">
                                                                Quantity
                                                            </span>

                                                            <p className="
                                                                mt-2
                                                                text-xs
                                                                sm:text-sm
                                                                font-semibold
                                                                text-gray-700
                                                            ">
                                                                {order.qty || 1}
                                                            </p>

                                                        </div>


                                                        {/* PAID AMOUNT */}
                                                        <div className="
                                                            bg-white
                                                            rounded-xl
                                                            border
                                                            border-[#EEE8DC]
                                                            p-3
                                                            sm:p-4
                                                        ">

                                                            <span className="
                                                                text-[9px]
                                                                sm:text-[10px]
                                                                uppercase
                                                                tracking-wider
                                                                font-bold
                                                                text-[#A17A17]
                                                            ">
                                                                Amount Paid
                                                            </span>

                                                            <p className="
                                                                mt-2
                                                                text-sm
                                                                sm:text-base
                                                                font-bold
                                                                text-[#A17A17]
                                                            ">
                                                                INR{" "}
                                                                {order.paidAmount ||
                                                                    order.amount}
                                                            </p>

                                                        </div>

                                                    </div>


                                                    {/* =================================================
                                                        STATUS
                                                    ================================================== */}
                                                    <div className="
                                                        mt-4
                                                        sm:mt-5
                                                        bg-white
                                                        rounded-xl
                                                        sm:rounded-2xl
                                                        border
                                                        border-[#EEE8DC]
                                                        p-4
                                                        sm:p-5
                                                    ">

                                                        <div className="
                                                            flex
                                                            items-start
                                                            gap-3
                                                        ">

                                                            <GoDotFill
                                                                className={`
                                                                    flex-shrink-0
                                                                    mt-1
                                                                    text-base
                                                                    ${isDelivered
                                                                        ? "text-green-500"
                                                                        : "text-amber-500"
                                                                    }
                                                                `}
                                                            />

                                                            <div className="
                                                                min-w-0
                                                                flex-1
                                                            ">

                                                                <h3 className="
                                                                    text-sm
                                                                    sm:text-base
                                                                    font-bold
                                                                    text-gray-800
                                                                ">
                                                                    {isDelivered
                                                                        ? "Order Placed & Confirmed"
                                                                        : "Pending Verification"
                                                                    }
                                                                </h3>

                                                                <p className="
                                                                    mt-1.5
                                                                    text-[11px]
                                                                    sm:text-xs
                                                                    md:text-sm
                                                                    text-gray-500
                                                                    leading-5
                                                                    sm:leading-6
                                                                ">
                                                                    {isDelivered
                                                                        ? "Your order has been verified and confirmed. A payment link has been dispatched to your mobile."
                                                                        : "Your order is currently undergoing inventory verification."
                                                                    }
                                                                </p>

                                                            </div>

                                                        </div>

                                                    </div>

                                                </div>

                                            </div>

                                        </div>

                                    </article>
                                );
                            })}

                        </div>
                    )}

                </div>
            </main>
        </Layouts>
    );
}

export default MyOrder;
